import { createRootRoute, useLocation, useNavigate } from '@tanstack/react-router';
import { Outlet } from '@/lib/router-components';
import { lazy, Suspense, useEffect } from 'react';
import { Toaster } from '../components/ui/toaster';
import { BugReportFab } from '../components/BugReportFab';
import { useEventBusStore } from '../stores/event-bus.store';
import { usePermissionDeniedStore } from '../stores/permissionDenied.store';
import { useStorageLimitExceededStore } from '../stores/storageLimitExceeded.store';
import { useWorkspaceBlockedStore } from '../stores/workspaceBlockedStore';
import { useWebSocket } from '../hooks/useWebSocket';
import { useAuthStore } from '../stores/auth.store';
import { useSidebarStore } from '../stores/sidebar.store';
import { useThemeStore } from '../stores/theme.store';
import { useLimitExceeded, setGlobalLimitExceededHandler } from '../hooks/useLimitExceeded';
import { usePermissions } from '../hooks/usePermissions';
import { refreshAuthSession } from '../lib/api';
import { captureWebError } from '../lib/sentry';
import {
  ScreenErrorFallback,
  ScreenLoadingFallback,
} from '../components/ui/screen-state';
import { t } from '../lib/i18n';
import { ConfirmDialogProvider } from '../hooks/useConfirmDialog';

const Header = lazy(() =>
  import('../components/Header').then((module) => ({ default: module.Header })),
);
const Sidebar = lazy(() =>
  import('../components/Sidebar').then((module) => ({ default: module.Sidebar })),
);
const ClientHeader = lazy(() =>
  import('../components/ClientHeader').then((module) => ({ default: module.ClientHeader })),
);
const ClientSidebar = lazy(() =>
  import('../components/ClientSidebar').then((module) => ({ default: module.ClientSidebar })),
);
const LimitExceededModal = lazy(() =>
  import('../components/LimitExceededModal').then((module) => ({
    default: module.LimitExceededModal,
  })),
);
const NoPermissionModal = lazy(() =>
  import('../components/NoPermissionModal').then((module) => ({
    default: module.NoPermissionModal,
  })),
);
const StorageLimitModal = lazy(() =>
  import('../components/StorageLimitModal').then((module) => ({
    default: module.StorageLimitModal,
  })),
);
const WorkspaceReadGuard = lazy(() =>
  import('../components/WorkspaceReadGuard').then((module) => ({
    default: module.WorkspaceReadGuard,
  })),
);
const WorkspaceRouteSync = lazy(() =>
  import('../components/WorkspaceRouteSync').then((module) => ({
    default: module.WorkspaceRouteSync,
  })),
);
const WorkspaceBlockedModal = lazy(() =>
  import('../components/WorkspaceBlockedModal').then((module) => ({
    default: module.WorkspaceBlockedModal,
  })),
);
const WorkspaceExpiredModal = lazy(() =>
  import('../components/WorkspaceExpiredModal').then((module) => ({
    default: module.WorkspaceExpiredModal,
  })),
);

export const Route = createRootRoute({
  component: () => {
    const toasts = useEventBusStore((state) => state.toasts);
    const removeToast = useEventBusStore((state) => state.removeToast);
    const user = useAuthStore((state) => state.user);
    const accessToken = useAuthStore((state) => state.accessToken);
    const setSession = useAuthStore((state) => state.setSession);
    const location = useLocation();
    const navigate = useNavigate();
    const isCollapsed = useSidebarStore((state) => state.isCollapsed);
    const theme = useThemeStore((state) => state.theme);
    const limitExceeded = useLimitExceeded();
    const { isLoading: isLoadingPermissions } = usePermissions();
    const permissionDenied = usePermissionDeniedStore((state) => ({
      isOpen: state.isOpen,
      permissionLabel: state.permissionLabel,
      close: state.close,
    }));
    const storageLimit = useStorageLimitExceededStore((state) => ({
      isOpen: state.isOpen,
      close: state.close,
    }));
    const workspaceBlocked = useWorkspaceBlockedStore((state) => ({
      isOpen: state.isOpen,
      code: state.code,
      workspaceId: state.workspaceId,
      workspaceName: state.workspaceName,
      close: state.close,
    }));
    const workspaceId = location.pathname.match(/^\/w\/([^/]+)/i)?.[1] ?? null;
    const isWorkspacePath = !!workspaceId;
    const isClientPath = (location.pathname ?? '').startsWith('/client');
    const shouldBlockWorkspaceContent = !!user && isWorkspacePath && isLoadingPermissions;

    const isWorkflowPresetEditorRoute = /^\/w\/[^/]+\/workflow-presets\/[^/]+$/i.test(
      location.pathname ?? '',
    );

    // Initialize WebSocket connection
    useWebSocket();

    // Apply theme class to document element when theme changes
    useEffect(() => {
      if (typeof document !== 'undefined') {
        if (theme === 'dark') {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      }
    }, [theme]);

    // Set global handler for limit exceeded modal
    useEffect(() => {
      setGlobalLimitExceededHandler(limitExceeded.open);
    }, [limitExceeded.open]);

    // Reidrata sessão em memória após reload para manter WS e headers Bearer.
    useEffect(() => {
      if (!user || accessToken) {
        return;
      }

      let cancelled = false;
      (async () => {
        try {
          const payload = await refreshAuthSession();
          if (!cancelled) {
            setSession(
              payload?.accessToken ?? null,
              payload?.refreshToken ?? null,
              payload?.csrfToken ?? null,
            );
          }
        } catch {
          // Sessão pode estar expirada; o fluxo de 401 já trata redirecionamento.
        }
      })();

      return () => {
        cancelled = true;
      };
    }, [user, accessToken, setSession]);

    // Redireciona cliente vs usuário comum e /w sem workspaceId
    useEffect(() => {
      if (!user) return;

      const path = location.pathname;
      const isClientUser = user.isClientUser === true;

      if (
        isClientUser &&
        (path.startsWith('/w/') ||
          path === '/w' ||
          path === '/w/' ||
          path.startsWith('/workspaces'))
      ) {
        navigate({ to: '/client' });
        return;
      }

      if (!isClientUser && path.startsWith('/client')) {
        navigate({ to: '/workspaces' });
        return;
      }

      if (!isClientUser && (path === '/w' || path === '/w/')) {
        navigate({ to: '/workspaces' });
        return;
      }
    }, [user, location.pathname, navigate]);

    const content = isWorkflowPresetEditorRoute ? (
      <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
        {user && (
          <Suspense fallback={<ScreenLoadingFallback inline />}>
            <WorkspaceRouteSync />
            <WorkspaceReadGuard />
          </Suspense>
        )}
        <main
          className="flex-1 min-h-screen flex flex-col min-w-0"
          style={{ position: 'relative', zIndex: 1, overflow: 'hidden' }}
        >
          {shouldBlockWorkspaceContent ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <ScreenLoadingFallback
                message={t('ui.loading.permissions', 'Carregando permissões...')}
              />
            </div>
          ) : (
            <>
              <Outlet />
              {user && <BugReportFab />}
            </>
          )}
        </main>
        <Toaster toasts={toasts} onRemove={removeToast} />
        <Suspense fallback={<ScreenLoadingFallback inline />}>
          {limitExceeded.limitType && (
            <LimitExceededModal
              isOpen={limitExceeded.isOpen}
              onClose={limitExceeded.close}
              limitType={limitExceeded.limitType}
              limit={limitExceeded.limit}
            />
          )}
          <NoPermissionModal
            isOpen={permissionDenied.isOpen}
            onClose={permissionDenied.close}
            permissionLabel={permissionDenied.permissionLabel}
          />
          <StorageLimitModal
            isOpen={storageLimit.isOpen}
            onClose={storageLimit.close}
          />
          {workspaceBlocked.workspaceId && (
            <>
              <WorkspaceBlockedModal
                isOpen={workspaceBlocked.isOpen && workspaceBlocked.code === 'WORKSPACE_BLOCKED'}
                workspaceId={workspaceBlocked.workspaceId}
                workspaceName={workspaceBlocked.workspaceName}
                onClose={workspaceBlocked.close}
              />
              <WorkspaceExpiredModal
                isOpen={workspaceBlocked.isOpen && workspaceBlocked.code === 'WORKSPACE_EXPIRED'}
                workspaceId={workspaceBlocked.workspaceId}
                workspaceName={workspaceBlocked.workspaceName}
                onClose={workspaceBlocked.close}
              />
            </>
          )}
        </Suspense>
      </div>
    ) : isClientPath ? (
      <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
        {user && (
          <Suspense fallback={<ScreenLoadingFallback inline />}>
            <ClientSidebar />
          </Suspense>
        )}
        <div
          className="flex-1 flex flex-col min-w-0"
          style={{ position: 'relative', overflow: 'visible', marginLeft: user ? 256 : 0 }}
        >
          {user && (
            <Suspense fallback={<ScreenLoadingFallback inline />}>
              <ClientHeader />
            </Suspense>
          )}
          <main
            className={`flex-1 ${user ? 'pt-24' : ''} bg-gray-50 dark:bg-gray-900`}
            style={{
              position: 'relative',
              zIndex: 1,
              overflow: 'auto',
              minHeight: 'calc(100vh - 96px)',
            }}
          >
            <Outlet />
          </main>
        </div>
        <Toaster toasts={toasts} onRemove={removeToast} />
      </div>
    ) : (
      <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
        {user && (
          <Suspense fallback={<ScreenLoadingFallback inline />}>
            <WorkspaceRouteSync />
            <WorkspaceReadGuard />
            <Sidebar />
          </Suspense>
        )}
        <div
          className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ease-in-out ${
            user
              ? isCollapsed
                ? 'lg:ml-[calc(5rem+2rem)]'
                : 'lg:ml-[calc(16rem+2rem)]'
              : ''
          }`}
          style={{ position: 'relative', overflow: 'visible' }}
        >
          {user && (
            <Suspense fallback={<ScreenLoadingFallback inline />}>
              <Header />
            </Suspense>
          )}
          <main
            className={`flex-1 ${user ? 'pt-24' : ''} bg-gray-50 dark:bg-gray-900`}
            style={{
              position: 'relative',
              zIndex: 1,
              overflow: 'auto',
              minHeight: 'calc(100vh - 96px)',
            }}
          >
            {shouldBlockWorkspaceContent ? (
              <div className="p-8">
                <ScreenLoadingFallback
                  message={t('ui.loading.permissions', 'Carregando permissões...')}
                />
              </div>
            ) : (
              <>
                <Outlet />
                {user && <BugReportFab />}
              </>
            )}
          </main>
        </div>
        <Toaster toasts={toasts} onRemove={removeToast} />
        <Suspense fallback={<ScreenLoadingFallback inline />}>
          {limitExceeded.limitType && (
            <LimitExceededModal
              isOpen={limitExceeded.isOpen}
              onClose={limitExceeded.close}
              limitType={limitExceeded.limitType}
              limit={limitExceeded.limit}
            />
          )}
          <NoPermissionModal
            isOpen={permissionDenied.isOpen}
            onClose={permissionDenied.close}
            permissionLabel={permissionDenied.permissionLabel}
          />
          <StorageLimitModal
            isOpen={storageLimit.isOpen}
            onClose={storageLimit.close}
          />
          {workspaceBlocked.workspaceId && (
            <>
              <WorkspaceBlockedModal
                isOpen={workspaceBlocked.isOpen && workspaceBlocked.code === 'WORKSPACE_BLOCKED'}
                workspaceId={workspaceBlocked.workspaceId}
                workspaceName={workspaceBlocked.workspaceName}
                onClose={workspaceBlocked.close}
              />
              <WorkspaceExpiredModal
                isOpen={workspaceBlocked.isOpen && workspaceBlocked.code === 'WORKSPACE_EXPIRED'}
                workspaceId={workspaceBlocked.workspaceId}
                workspaceName={workspaceBlocked.workspaceName}
                onClose={workspaceBlocked.close}
              />
            </>
          )}
        </Suspense>
      </div>
    );

    return <ConfirmDialogProvider>{content}</ConfirmDialogProvider>;
  },
  errorComponent: ({ error }) => {
    useEffect(() => {
      captureWebError(error, {
        source: 'router_error_component',
        path: window.location.pathname,
      });
    }, [error]);

    const message =
      error instanceof Error ? error.message : 'Erro inesperado ao carregar a tela.';

    return (
      <ScreenErrorFallback
        title={t('ui.error.title', 'Ocorreu um erro na interface')}
        message={message}
        retryLabel={t('ui.error.retry', 'Tentar novamente')}
        onRetry={() => window.location.reload()}
      />
    );
  },
});
